import { memo, useState } from 'react'
import { observer } from 'mobx-react'
import { useInView } from 'react-intersection-observer'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import Grid from '@mui/material/Grid'
import useMediaQuery from '@mui/material/useMediaQuery'
import { useTheme } from '@mui/material/styles'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'

import { LoadingMode } from 'components/LoadingProvider'
import HotlineFallback from 'components/HotlineFallback'
import { useErrorState } from 'context/Error/hook'
import CategoryLayout from 'components/Layout/Category'
import { useLoadingMode } from 'stores/utils/hooks/useLoadingMode'
import { trackInAmplitude } from 'utils/tracking/amplitude'
import { AmplitudeEvent } from 'utils/tracking/amplitude/amplitude.interface'
import { useDebouncedEffect } from 'hooks/useDebouncedEffect'
import { Button, Link, Theme, Typography } from '@mui/material'
import { NavigateFunction } from 'react-router-dom'
import useEmotionStyles from 'hooks/useEmotionStyles'
import { useStores } from 'stores/utils/hooks/useStores'
import { PREFILL_PAGE_NAMES } from 'utils/pages'
import ResendPin from './ResendPin'
import { PIN_LENGTH } from './AuthenticatePin'
import TimeoutTrigger from './TimeoutTrigger'
import { trackPinCheck } from './utils'
import { trackTeal } from 'utils/tracking'
import { COOKIE_KEY_FCID, getCookie } from 'utils/cookie'
import { isPrefillAuthCountExceeded } from './utils'
import { PrefillErrorKind } from 'stores/PrefillStore'
import { useClientDetails } from 'stores/utils/hooks/useClientDetails'
import { logAndTrackError } from 'utils/log/logAndTrackError'
import { AxiosError } from 'axios'
import { useAsyncEffect } from 'hooks/useAsyncEffect'
import PreviousPageErrors from 'components/PageErrors/PreviousPageErrors'

export const CONTINUE_BUTTON_TEXT: Record<
  (typeof PREFILL_PAGE_NAMES)[number],
  string
> = {
  requestPin: 'PIN anfordern',
  authenticatePin: 'PIN prüfen',
  verifiedPin: 'Direkt zu den Angeboten'
}

export const SECONDARY_BUTTON_TEXT: Record<
  (typeof PREFILL_PAGE_NAMES)[number],
  string
> = {
  requestPin: 'Daten manuell eingeben',
  authenticatePin: 'Daten manuell eingeben',
  verifiedPin: 'Vorausgefüllte Daten überprüfen'
}

export const getButtonSizes = (isBackButtonHidden: boolean) => {
  const sizeOfContinueButton = 12
  const sizeOfSecondaryButton = isBackButtonHidden ? 12 : 8
  const sizeOfBackButton = isBackButtonHidden ? 0 : 4

  return {
    sizeOfContinueButton,
    sizeOfSecondaryButton,
    sizeOfBackButton
  }
}

type Props = {
  navigate: NavigateFunction
  currentPageInSession: string | null
  continueButtonColor: 'primary' | 'secondary'
  backButtonVariant: 'text' | 'outlined' | 'contained'
  isSmava: boolean
  onBack?: () => void
  onNext: () => void
}

const styles = (theme: Theme) => ({
  flexBasisGrid: {
    flexBasis: 'auto'
  },
  button: {
    maxHeight: '48px',

    '&.Mui-disabled': {
      backgroundColor: `${theme.palette.action.disabledBackground} !important`
    }
  }
})

const PrefillFooter = observer(
  ({
    onNext,
    currentPageInSession,
    continueButtonColor,
    backButtonVariant,
    onBack,
    navigate
  }: Props) => {
    const {
      state: { message },
      hasCooldown
    } = useErrorState()
    const {
      fieldErrors,
      prefill,
      traversal: {
        data: {
          system: { advertisementId }
        },
        traversalId
      }
    } = useStores()
    const { ref, inView } = useInView()
    const clientDetails = useClientDetails()

    useDebouncedEffect(
      () => {
        if (inView) {
          trackInAmplitude(AmplitudeEvent.CtaIsVisible)
        }
      },
      300,
      [inView]
    )

    const loadingMode = useLoadingMode()

    const { flexBasisGrid, button } = useEmotionStyles(styles, 'primary')
    const theme = useTheme()
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

    const sendPin = async () => {
      try {
        await prefill.getPrefillPin(
          advertisementId,
          traversalId,
          prefill.isPhoneMobile ? 'sms' : 'email'
        )
        prefill.setIsPrefillSkipped(false)
        onNext()
      } catch (err) {
        const errorObj = err as AxiosError
        const status: number | undefined = errorObj?.status

        switch (status) {
          case 429:
            prefill.setIsError(true)
            prefill.setErrorKind(PrefillErrorKind.MAX_ATTEMPTS)
            break
          case 500:
            prefill.setIsError(true)
            prefill.setErrorKind(PrefillErrorKind.TECHNICAL)
            break
          default:
            prefill.setIsError(true)
            prefill.setErrorKind(PrefillErrorKind.TECHNICAL)
            break
        }

        logAndTrackError(
          new Error(`/prefill/pin/send failed. Error status: ${status}`)
        )
      }
    }

    const trackPrefill = (
      event: AmplitudeEvent,
      payload?: Record<string, unknown>
    ) => {
      trackTeal({
        transaction: {
          event
        },
        payload,
        context: {
          Optimizely: { userId: getCookie(COOKIE_KEY_FCID) },
          userAttributes: {
            advertisementId
          }
        }
      })
    }

    const handleContinueButtonClick = async () => {
      trackInAmplitude(AmplitudeEvent.PrefillPrimaryButtonClicked, {
        isError: prefill.isError
      })

      if (prefill.isError) {
        onNext()
        return
      }

      if (currentPageInSession === 'requestPin') {
        trackPrefill(AmplitudeEvent.PrefillRequestPinClicked, {
          method: prefill.isPhoneMobile ? 'sms' : 'email'
        })
        trackInAmplitude(AmplitudeEvent.PrefillRequestPinClicked)
        sendPin()
        return
      }

      if (currentPageInSession === 'authenticatePin') {
        trackPrefill(AmplitudeEvent.PrefillAuthPinClicked)
        const authPinExceeded = isPrefillAuthCountExceeded()
        if (authPinExceeded) {
          prefill.setIsError(true)
          prefill.setErrorKind(PrefillErrorKind.MAX_ATTEMPTS)
          return
        }
        try {
          await prefill.postPrefillPinAuth(
            advertisementId,
            traversalId,
            prefill.pin
          )
          onNext()
        } catch (err) {
          logAndTrackError(err as Error)
        }
        return
      }

      if (currentPageInSession === 'verifiedPin') {
        trackPrefill(AmplitudeEvent.PrefillGoToOffersClicked)
        triggerOnNextWithFinalize()
        return
      }
    }

    const handleSecondaryButtonClick = () => {
      trackInAmplitude(AmplitudeEvent.PrefillSecondaryButtonClicked)

      if (currentPageInSession === 'requestPin') {
        trackPrefill(AmplitudeEvent.PrefillManualEntryClicked)
      }

      if (currentPageInSession === 'authenticatePin') {
        trackPinCheck('escape')
      }

      if (currentPageInSession === 'verifiedPin') {
        prefill.setIsFinalForced(false)
        trackPrefill(AmplitudeEvent.PrefillCheckDataClicked)
      }

      prefill.setIsPrefillSkipped(true)
      onNext()
    }

    const handleBackButtonClick = () => {
      if (currentPageInSession === 'requestPin') {
        trackPrefill(AmplitudeEvent.PrefillBackClicked)
      }

      onBack?.()
    }

    const triggerOnNextWithFinalize = () => {
      prefill.setIsFinalForced(true)
      onNext()
    }

    const handleTimeout = () => {
      trackPrefill(AmplitudeEvent.PrefillGoToOffersAfterTimeout)
      triggerOnNextWithFinalize()
    }

    const continueButtonText = prefill.isError
      ? 'Weiter mit manueller Eingabe'
      : CONTINUE_BUTTON_TEXT[currentPageInSession ?? 'Weiter']

    const secondaryButtonText =
      SECONDARY_BUTTON_TEXT[currentPageInSession ?? 'Weiter']

    const isBackButtonHidden =
      currentPageInSession === 'verifiedPin' ||
      currentPageInSession === 'authenticatePin' ||
      prefill.isError

    const isCaptionVisible = currentPageInSession === 'requestPin'

    const { sizeOfContinueButton, sizeOfSecondaryButton, sizeOfBackButton } =
      getButtonSizes(isBackButtonHidden)

    const [previousErrorPageName, setPreviousErrorPageName] = useState<string>()

    useAsyncEffect(async () => {
      const previousErrorPageNameFlag = await fieldErrors.findFirstErrorPage()

      setPreviousErrorPageName(previousErrorPageNameFlag)
    }, [loadingMode])

    if (previousErrorPageName) {
      trackInAmplitude(AmplitudeEvent.PreviousPageErrorNavigationShown)
      return (
        <PreviousPageErrors
          navigate={navigate}
          pageName={previousErrorPageName}
        />
      )
    }

    return (
      <CategoryLayout ref={ref}>
        {currentPageInSession === 'verifiedPin' && !prefill.isError && (
          <TimeoutTrigger onTimeout={handleTimeout} />
        )}
        {message && <HotlineFallback message={message} />}
        <Grid
          container
          spacing={1}
          direction={isMobile ? 'column' : 'row-reverse'}
          justifyContent="center"
          alignItems="center"
        >
          <Grid
            className={flexBasisGrid}
            container
            item
            spacing={1}
            direction={isMobile ? 'column' : 'row-reverse'}
            xs={12}
          >
            {/* CONTINUE BUTTON */}
            <Grid item xs={12} sm={sizeOfContinueButton} mb={1}>
              <Button
                className={button}
                size="large"
                fullWidth
                onClick={handleContinueButtonClick}
                id="next-submit"
                name="next-submit"
                variant="contained"
                color={continueButtonColor}
                endIcon={
                  loadingMode === LoadingMode.None ? (
                    <ArrowForwardIcon />
                  ) : undefined
                }
                disabled={
                  hasCooldown ||
                  loadingMode === LoadingMode.Blocking ||
                  (currentPageInSession === 'authenticatePin' &&
                    (prefill.pin.length !== PIN_LENGTH ||
                      prefill.isPinInvalid) &&
                    !prefill.isError)
                }
              >
                {continueButtonText}
              </Button>
            </Grid>

            <Grid item xs={12}>
              {currentPageInSession === 'authenticatePin' &&
                !prefill.isError && (
                  <Grid item xs={12} mt={0} mb={2} pt={0}>
                    <ResendPin
                      setPin={() => {}}
                      isMobileHidden={!prefill.isPhoneMobile}
                    />
                  </Grid>
                )}
            </Grid>

            {/* SECONDARY BUTTON */}
            {!prefill.isError && (
              <Grid
                item
                xs={12}
                sm={sizeOfSecondaryButton}
                data-testid="grid-item-next-submit-container"
                display="flex"
                alignItems="center"
                fontWeight={500}
              >
                <Button
                  className={button}
                  size="large"
                  fullWidth
                  variant={backButtonVariant}
                  color="primary"
                  disabled={hasCooldown}
                  onClick={handleSecondaryButtonClick}
                  endIcon={
                    loadingMode === LoadingMode.None ? (
                      <ArrowForwardIcon />
                    ) : undefined
                  }
                  sx={{
                    height: '48px',
                    fontSize: 16
                  }}
                >
                  {secondaryButtonText}
                </Button>
              </Grid>
            )}

            {/* BACK BUTTON */}
            {!isBackButtonHidden && (
              <Grid item xs={12} sm={sizeOfBackButton}>
                <Button
                  className={button}
                  size="large"
                  onClick={handleBackButtonClick}
                  type="button"
                  id="back-submit"
                  name="back-submit"
                  color="primary"
                  sx={{
                    height: '48px',
                    fontSize: 16
                  }}
                  variant={backButtonVariant}
                  startIcon={<ArrowBackIcon />}
                  fullWidth
                >
                  Zurück
                </Button>
              </Grid>
            )}

            {/* CAPTION */}
            {isCaptionVisible && !prefill.isError && (
              <Grid item>
                <Typography variant="caption">
                  Ich willige ein, dass meine Daten aus der jüngsten Anfrage
                  innerhalb der letzten 4 Jahre zum Vorausfüllen der
                  Antragsstrecke verwendet werden. Ich kann meine freiwillige
                  Einwilligung jederzeit mit Wirkung für die Zukunft widerrufen.{' '}
                  <Link
                    onClick={() =>
                      trackInAmplitude(
                        AmplitudeEvent.PrefillDataProtectionClicked
                      )
                    }
                    href={clientDetails?.urls?.dataProtection}
                    target="_blank"
                    rel="noopener noreferrer"
                    underline="hover"
                  >
                    Mehr Infos
                  </Link>
                  .
                </Typography>
              </Grid>
            )}
          </Grid>
        </Grid>
      </CategoryLayout>
    )
  }
)

export default memo(PrefillFooter)
